import { CandleData, Timeframe } from "../types";

export interface ProcessTickResult {
    updatedCandles: CandleData[];
    isNewCandle: boolean;
}

/**
 * Calculates the candle bucket start time for a given tick and timeframe.
 */
export const getCandleStartTime = (tickTime: number, timeframe: Timeframe): number => {
    const tfMap: Record<string, number> = {
        '1m': 60000, '5m': 300000, '15m': 900000, '30m': 1800000,
        '1h': 3600000, '4h': 14400000, '1d': 86400000, '1w': 604800000
    };
    const tfMs = tfMap[timeframe] || 3600000;
    return Math.floor(tickTime / tfMs) * tfMs;
};

/**
 * Processes a single tick to update or create a candle in the series.
 * Pure function for easier testing.
 */
export const processReplayTick = (
    currentCandles: CandleData[],
    tick: { time: number; price: number; volume: number },
    timeframe: Timeframe
): ProcessTickResult => {
    if (currentCandles.length === 0) {
        // Fallback if no history (shouldn't happen in replay usually, but safe to handle)
        const startTime = getCandleStartTime(tick.time, timeframe);
        const newCandle: CandleData = {
            time: startTime,
            open: tick.price,
            high: tick.price,
            low: tick.price,
            close: tick.price,
            volume: tick.volume,
            dateStr: new Date(startTime).toLocaleTimeString()
        };
        return { updatedCandles: [newCandle], isNewCandle: true };
    }

    const lastCandle = currentCandles[currentCandles.length - 1];
    const tickCandleStartTime = getCandleStartTime(tick.time, timeframe);

    if (tickCandleStartTime > lastCandle.time) {
        // NEW CANDLE
        const newCandle: CandleData = {
            time: tickCandleStartTime,
            open: tick.price,
            high: tick.price,
            low: tick.price,
            close: tick.price,
            volume: tick.volume,
            dateStr: new Date(tickCandleStartTime).toLocaleTimeString()
        };
        return { 
            updatedCandles: [...currentCandles, newCandle], 
            isNewCandle: true 
        };
    } else {
        // UPDATE EXISTING CANDLE
        const updatedLast = { ...lastCandle };
        updatedLast.close = tick.price;
        updatedLast.high = Math.max(updatedLast.high, tick.price);
        updatedLast.low = Math.min(updatedLast.low, tick.price);
        updatedLast.volume += tick.volume;

        const newCandles = [...currentCandles];
        newCandles[newCandles.length - 1] = updatedLast;
        
        return { 
            updatedCandles: newCandles, 
            isNewCandle: false 
        };
    }
};